/**
 * Same-day appointment lines — pure helpers for the precollect flow.
 *
 * Phase 1 of PORTAL-SAME-DAY-APPOINTMENTS (build plan §3.2). The
 * `/checkin/balance/:id` response now carries `same_day_appointments[]`
 * (anchor included, sorted by start time). `PayPage` renders one
 * `EstimatedResponsibility` card per entry, anchor first.
 *
 * Everything here is deliberately free of React so the two behaviours
 * that matter most can be unit-tested directly:
 *
 *   1. **Fallback.** When the field is absent (older backend) or empty,
 *      synthesize a single line from the legacy scalar estimate fields
 *      so the shipped single-appointment behaviour is unchanged.
 *   2. **Ordering.** Anchor first, then the backend's start-time order.
 *
 * Money semantics are unchanged from the single-appointment flow:
 * `estimate_remaining_minor_units` is what the patient can pay, and
 * each line is paid all-or-nothing.
 *
 * **Phase 2 (build plan §4.5)** — one card entry pays every CHECKED line
 * plus the prior balance in ONE charge. `POST /checkin/payment` takes an
 * additive `lines[]`; `buildChargeLines` / `sumChargeLines` build that
 * payload from the checked cards, and `supportsBatchCharge` says whether
 * the backend in hand advertises the same-day contract at all (older
 * backends omit `same_day_appointments` and keep the Phase 1 shape).
 */
import type {
  DayAccumulators,
  FetchCheckinBalanceResponse,
  PrecollectChargeLine,
  SameDayAppointment,
} from '../../api/types';
import { STRIPE_USD_MIN_CENTS } from './stripeMinimum';

/**
 * True when the backend sent `same_day_appointments` (non-empty), i.e.
 * it speaks the Phase 1+ contract and therefore accepts `lines` on
 * `POST /checkin/payment` (Phase 2 shipped on the same backend main).
 * False → older backend: the page must keep the Phase 1 single-
 * appointment request shape (no `lines`).
 */
export function supportsBatchCharge(checkin: FetchCheckinBalanceResponse): boolean {
  const list = checkin.same_day_appointments;
  return Array.isArray(list) && list.length > 0;
}

/**
 * The `lines[]` payload for a batch charge: every CHECKED, chargeable
 * line with its FULL remaining estimate (all-or-nothing per line).
 * Unchecked cards are omitted rather than sent as zero lines; a line
 * that is not chargeable (pending / settled / sub-minimum / cancelled)
 * is never emitted even if its id is in `selectedIds`. Order follows
 * `lines` (anchor first).
 */
export function buildChargeLines(
  lines: readonly SameDayAppointment[],
  selectedIds: ReadonlySet<number>,
): PrecollectChargeLine[] {
  return lines
    .filter((line) => selectedIds.has(line.appointment_id) && isLineChargeable(line))
    .map((line) => ({
      appointment_id: line.appointment_id,
      estimate_amount_minor_units: line.estimate_remaining_minor_units,
    }));
}

/** Σ `estimate_amount_minor_units` — what goes in the top-level estimate component. */
export function sumChargeLines(lines: readonly PrecollectChargeLine[]): number {
  return lines.reduce((acc, line) => acc + line.estimate_amount_minor_units, 0);
}

/** Default selection: every chargeable line checked. */
export function defaultSelectedEstimateIds(
  lines: readonly SameDayAppointment[],
): Set<number> {
  return new Set(lines.filter(isLineChargeable).map((line) => line.appointment_id));
}

/**
 * Status priority used to group sibling cards (anchor excluded — it's
 * always pinned first regardless of its own status). Lower sorts first:
 * actionable (has a remaining balance, chargeable or sub-minimum) before
 * pending (no estimate yet) before settled/covered. Reported bug: a
 * settled sibling could land between two unpaid ones because the raw
 * list is only start-time ordered — patients read that as "wrong order".
 */
function estimateLineGroup(line: SameDayAppointment): number {
  if (lineHasRemaining(line)) return 0;
  if (!line.has_estimate) return 1;
  return 2; // settled: has an estimate, nothing remaining
}

/** Stable sort by status group, preserving start-time order within a group. */
function groupSiblings(lines: readonly SameDayAppointment[]): SameDayAppointment[] {
  return [...lines]
    .map((line, index) => ({ line, index }))
    .sort((a, b) => {
      const byGroup = estimateLineGroup(a.line) - estimateLineGroup(b.line);
      return byGroup !== 0 ? byGroup : a.index - b.index;
    })
    .map(({ line }) => line);
}

/**
 * Resolve the list of estimate lines to render for a checkin response.
 *
 * - `same_day_appointments` present and non-empty → anchor first, then
 *   its siblings grouped by status (actionable, then pending, then
 *   settled/covered) so a covered visit never sits between two unpaid
 *   ones. Within each group, the backend's start-time order is kept
 *   (stable sort). If no entry is flagged as anchor (defensive — the
 *   contract says exactly one is) the backend order is kept as-is.
 * - Field absent / empty → one synthesized line from the legacy scalars,
 *   flagged `is_anchor: true` and `has_estimate: true` so it renders
 *   exactly as the pre-Phase-1 single block did.
 */
export function resolveEstimateLines(
  checkin: FetchCheckinBalanceResponse,
): SameDayAppointment[] {
  const list = checkin.same_day_appointments;
  if (Array.isArray(list) && list.length > 0) {
    const anchorIndex = list.findIndex((line) => line.is_anchor);
    if (anchorIndex < 0) return groupSiblings(list);
    const anchor = list[anchorIndex] as SameDayAppointment;
    const siblings = list.filter((_, i) => i !== anchorIndex);
    return [anchor, ...groupSiblings(siblings)];
  }
  return [legacyLineFromScalars(checkin)];
}

/**
 * Older-backend fallback: the single-appointment shape, lifted into a
 * one-element list. Field-for-field the same values the legacy
 * `EstimatedResponsibility` read straight off the response.
 */
export function legacyLineFromScalars(
  checkin: FetchCheckinBalanceResponse,
): SameDayAppointment {
  return {
    appointment_id: checkin.appointment_id,
    is_anchor: true,
    appointment_date: checkin.appointment_date,
    appointment_date_formatted: checkin.appointment_date_formatted,
    appointment_type_name: checkin.appointment_type_name,
    provider_name: checkin.provider_name,
    facility_name: checkin.facility_name,
    has_estimate: true,
    appointment_cancelled: false,
    deductible_minor_units: checkin.deductible_minor_units,
    copay_minor_units: checkin.copay_minor_units,
    coinsurance: checkin.coinsurance,
    insurance_estimated_pay_minor_units:
      checkin.insurance_estimated_pay_minor_units,
    estimate_total_minor_units: checkin.estimate_total_minor_units,
    estimate_paid_minor_units: checkin.estimate_paid_minor_units,
    estimate_remaining_minor_units: checkin.estimate_remaining_minor_units,
  };
}

/**
 * Optimistic "just paid" overlay. After a successful charge the webhook
 * posts the ledger asynchronously, so the server still reports the old
 * remaining for a while; `PayPage` keeps a per-appointment map of what
 * was just paid (sessionStorage) and applies it here so the card shows
 * the post-payment state immediately.
 */
export function applyJustPaidEstimates(
  lines: SameDayAppointment[],
  justPaid: Readonly<Record<number, number>>,
): SameDayAppointment[] {
  return lines.map((line) => {
    const paidNow = justPaid[line.appointment_id] ?? 0;
    if (paidNow <= 0) return line;
    return {
      ...line,
      estimate_paid_minor_units: line.estimate_paid_minor_units + paidNow,
      estimate_remaining_minor_units: Math.max(
        0,
        line.estimate_remaining_minor_units - paidNow,
      ),
    };
  });
}

/**
 * Optimistic "just paid" overlay for the DAY total, matching
 * `applyJustPaidEstimates` at the line level.
 *
 * Needed for the same reason: after a successful charge the webhook posts the
 * ledger asynchronously, so `/checkin/balance` keeps reporting the pre-payment
 * `day_owed_minor_units` for a while. Without this the merged card would tell a
 * patient who has just paid that they still owe the full day.
 *
 * This subtracts only what THIS session recorded as paid for the day's own
 * appointments, clamped at zero. It is a display overlay over a known-stale
 * server value, not a recalculation of what is owed — the day formula stays
 * entirely in appointment-api.
 */
export function applyJustPaidToDay(
  day: DayAccumulators | null,
  lines: readonly SameDayAppointment[],
  justPaid: Readonly<Record<number, number>>,
): DayAccumulators | null {
  if (day === null || day.day_owed_minor_units === null) return day;
  const paidNow = lines.reduce(
    (acc, line) => acc + (justPaid[line.appointment_id] ?? 0),
    0,
  );
  if (paidNow <= 0) return day;
  return {
    ...day,
    day_owed_minor_units: Math.max(0, day.day_owed_minor_units - paidNow),
  };
}

/**
 * True when the line's remaining estimate can be charged online: it has
 * an estimate, is not cancelled (the backend rejects money on a cancelled
 * line with APPOINTMENT_CANCELLED, and normally omits such lines
 * altogether), and the remaining clears the Stripe minimum.
 */
export function isLineChargeable(line: SameDayAppointment): boolean {
  return (
    line.has_estimate &&
    isLineEstimable(line) &&
    !line.appointment_cancelled &&
    line.estimate_remaining_minor_units >= STRIPE_USD_MIN_CENTS
  );
}

/**
 * True when the backend could actually PRICE this visit (BFF 0.2.0
 * `estimable`).
 *
 * `false` is a refusal: no copay and no coinsurance, a missing or negative
 * deductible, no allowed amount, no estimate row, or a row whose inputs
 * contradict its own status. In every one of those the stored estimate is NULL,
 * which arrives as `0` — which, read as "remaining is zero", rendered
 * *"this visit's estimate is already covered"* for a visit nobody could price.
 * That was a live bug, and it is why every consumer branches here and never on
 * `estimate_remaining_minor_units === 0`.
 *
 * `undefined` (older BFF, or the `legacyLineFromScalars` fallback) is treated as
 * TRUE: that backend has no opinion, and today's behaviour is the baseline.
 */
export function isLineEstimable(line: SameDayAppointment): boolean {
  return line.estimable !== false;
}

/**
 * How the day's money should be treated, from the presence and state of
 * `day_accumulators`. THREE states, deliberately not two:
 *
 * - `'legacy'` — the block is absent, so this is a pre-0.2.0 BFF. The stored
 *   per-appointment estimates are still the contract; behave exactly as before.
 * - `'priced'` — the day was priced. Render `day_owed_minor_units` as what is
 *   owed at check-in and charge the per-line day-adjusted figures.
 * - `'unpriced'` — a 0.2.0 BFF that could not price the day (appointment-api
 *   unreachable, or the rows disagreed). The charge endpoint WILL refuse the
 *   estimate component, so the FE must not offer one.
 *
 * Collapsing `'legacy'` and `'unpriced'` into one "no day total" state is the
 * mistake to avoid: it would take the legacy path against a BFF that then 422s
 * every payment. This holds on a SINGLE-appointment day too — the BFF does not
 * exempt it, because the out-of-pocket cap can make the stored estimate higher
 * than what is actually owed.
 */
export type DayPricingState = 'legacy' | 'priced' | 'unpriced';

export function resolveDayAccumulators(
  checkin: FetchCheckinBalanceResponse,
): DayAccumulators | null {
  return checkin.day_accumulators ?? null;
}

export function dayPricingState(day: DayAccumulators | null): DayPricingState {
  if (day === null) return 'legacy';
  return day.complete ? 'priced' : 'unpriced';
}

/**
 * The lines whose estimate can go into a charge, given the day's pricing state.
 * On an `'unpriced'` day that is NONE of them, however chargeable they look on
 * their own: the backend refuses the estimate component outright.
 */
export function chargeableLinesForDay(
  lines: readonly SameDayAppointment[],
  state: DayPricingState,
): SameDayAppointment[] {
  if (state === 'unpriced') return [];
  return lines.filter(isLineChargeable);
}

/**
 * The one header checkbox: all-or-nothing over the CHARGEABLE subset.
 * `next === true` selects every chargeable line, `false` clears the selection.
 * Pending / settled / sub-minimum / cancelled / unpriceable lines are never
 * selected, exactly as `isLineChargeable` already decides.
 */
export function toggleAllChargeable(
  lines: readonly SameDayAppointment[],
  next: boolean,
): Set<number> {
  return next ? defaultSelectedEstimateIds(lines) : new Set<number>();
}

/** True when every chargeable line is selected (and there is at least one). */
export function allChargeableSelected(
  lines: readonly SameDayAppointment[],
  selectedIds: ReadonlySet<number>,
): boolean {
  const chargeable = lines.filter(isLineChargeable);
  return (
    chargeable.length > 0 &&
    chargeable.every((line) => selectedIds.has(line.appointment_id))
  );
}

/**
 * Split a backend-formatted clinic-local datetime (`"June 8, 2026 at 10:08 AM"`)
 * into its date and time halves, so the merged day card can print the date ONCE
 * in its header and a bare time against each service.
 *
 * This is a pure string split on `" at "` — the separator the backend's own
 * formatter writes. It deliberately does NOT parse the value: passing it through
 * `new Date()` or `Intl.DateTimeFormat` would reinterpret it in the BROWSER's
 * timezone, and the clinic's local time is the only one that matters (FE ledger
 * #49). If the separator is absent (a backend that changes the format), the
 * whole string is returned as the date and the time is null, so the card still
 * renders something true rather than a mangled fragment.
 */
export function splitFormattedDateTime(
  formatted: string | null | undefined,
): { date: string; time: string | null } {
  const value = (formatted ?? '').trim();
  const at = value.indexOf(' at ');
  if (at < 0) return { date: value, time: null };
  return { date: value.slice(0, at), time: value.slice(at + 4) };
}

/** True when the line still has ANY remaining estimate (chargeable or sub-minimum). */
export function lineHasRemaining(line: SameDayAppointment): boolean {
  return line.has_estimate && line.estimate_remaining_minor_units > 0;
}

/**
 * Parse the sessionStorage `justPaidEstimates` map. Tolerates the
 * pre-Phase-1 single-number key being absent; a malformed value yields
 * an empty map rather than throwing.
 */
export function parseJustPaidEstimates(raw: string | null): Record<number, number> {
  if (!raw) return {};
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    const out: Record<number, number> = {};
    for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
      const id = Number(k);
      const cents = typeof v === 'number' ? v : Number(v);
      if (Number.isFinite(id) && Number.isFinite(cents) && cents > 0) {
        out[id] = cents;
      }
    }
    return out;
  } catch {
    return {};
  }
}
